// src/services/indexedDB/utils/cache-manager.ts
// 快取管理工具函數

import { logDev, logWarn } from '../../../utils/logger';

/**
 * 版本化實體介面
 * 需要進行版本比較的實體應包含 updatedAt 欄位
 */
interface Versioned {
  id: string;
  updatedAt?: Date | number | string;
}

/**
 * 比較兩個實體的更新時間
 * 用於衝突檢測
 *
 * @param local - 本地實體
 * @param remote - 遠端實體
 * @returns true 表示本地版本較新
 */
export const isLocalNewer = (local: Versioned, remote: Versioned): boolean => {
  if (!local.updatedAt || !remote.updatedAt) {
    return false; // 如果缺少時間戳，預設使用遠端版本
  }

  const localTime = new Date(local.updatedAt).getTime();
  const remoteTime = new Date(remote.updatedAt).getTime();

  return localTime > remoteTime;
};

/**
 * 衝突檢測：決定是否應該跳過遠端資料
 *
 * @param local - 本地實體（可能不存在）
 * @param remote - 遠端實體
 * @returns true 表示應該跳過遠端資料
 */
export const shouldSkipRemote = (
  local: Versioned | null | undefined,
  remote: Versioned
): boolean => {
  if (!local) {
    return false; // 本地沒有資料，接受遠端
  }

  if (isLocalNewer(local, remote)) {
    logDev('[Cache] Skipping older remote data', {
      id: remote.id,
      localUpdatedAt: local.updatedAt,
      remoteUpdatedAt: remote.updatedAt,
    });
    return true;
  }

  return false;
};

/**
 * 最近刪除的 ID 管理器
 * 用於防止已刪除的實體被重新載入
 */
class RecentlyDeletedManager {
  private deletedIds = new Set<string>();
  private readonly retentionMs: number;

  constructor(retentionMs: number = 60000) {
    // 預設保留 60 秒
    this.retentionMs = retentionMs;
  }

  /**
   * 標記 ID 為已刪除
   */
  markAsDeleted(id: string): void {
    this.deletedIds.add(id);

    // 設定自動清除
    setTimeout(() => {
      this.deletedIds.delete(id);
    }, this.retentionMs);
  }

  /**
   * 檢查 ID 是否最近被刪除
   */
  isRecentlyDeleted(id: string): boolean {
    return this.deletedIds.has(id);
  }

  /**
   * 清除所有記錄
   */
  clear(): void {
    this.deletedIds.clear();
  }
}

// 為不同實體類型創建刪除管理器
const deletionManagers = new Map<string, RecentlyDeletedManager>();

/**
 * 獲取指定實體類型的刪除管理器
 */
const getDeleteManager = (entityType: string): RecentlyDeletedManager => {
  if (!deletionManagers.has(entityType)) {
    deletionManagers.set(entityType, new RecentlyDeletedManager());
  }
  return deletionManagers.get(entityType)!;
};

/**
 * 標記實體為最近刪除
 *
 * @param entityType - 實體類型（如 'TimeBox', 'Task'）
 * @param id - 實體 ID
 */
export const markAsRecentlyDeleted = (entityType: string, id: string): void => {
  const manager = getDeleteManager(entityType);
  manager.markAsDeleted(id);
  logDev(`[Cache] Marked ${entityType} as recently deleted`, { id });
};

/**
 * 檢查實體是否最近被刪除
 *
 * @param entityType - 實體類型
 * @param id - 實體 ID
 * @returns 是否最近被刪除
 */
export const isRecentlyDeleted = (entityType: string, id: string): boolean => {
  const manager = getDeleteManager(entityType);
  return manager.isRecentlyDeleted(id);
};

/**
 * 清除指定類型的刪除記錄
 *
 * @param entityType - 實體類型
 */
export const clearRecentlyDeleted = (entityType: string): void => {
  const manager = getDeleteManager(entityType);
  manager.clear();
};

/**
 * 過濾出應該保存的實體
 * 排除最近被刪除的和本地版本較新的
 *
 * @param entities - 遠端實體列表
 * @param getLocal - 獲取本地實體的函數
 * @param entityType - 實體類型
 * @returns 應該保存的實體列表
 */
export const filterEntitiesToSave = async <T extends Versioned>(
  entities: T[],
  getLocal: (id: string) => Promise<T | null>,
  entityType: string
): Promise<T[]> => {
  const toSave: T[] = [];

  for (const entity of entities) {
    // 檢查是否最近被刪除
    if (isRecentlyDeleted(entityType, entity.id)) {
      logDev(`[Cache] Skipping recently deleted ${entityType}`, { id: entity.id });
      continue;
    }

    // 檢查本地版本
    const local = await getLocal(entity.id);
    if (shouldSkipRemote(local, entity)) {
      continue;
    }

    toSave.push(entity);
  }

  return toSave;
};

/**
 * 生成臨時 ID
 * 用於離線創建的實體
 *
 * @param prefix - ID 前綴（如 'temp-timebox-'）
 * @returns 臨時 ID
 */
export const generateTempId = (prefix: string = 'temp-'): string => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 9);
  return `${prefix}${timestamp}-${random}`;
};

/**
 * 檢查是否為臨時 ID
 *
 * @param id - 實體 ID
 * @returns 是否為臨時 ID
 */
export const isTempId = (id: string): boolean => {
  return id.startsWith('temp-');
};

/**
 * 批次操作錯誤處理
 * 記錄錯誤但不中斷整個批次
 *
 * @param operation - 操作描述
 * @param id - 實體 ID
 * @param error - 錯誤物件
 */
export const handleBatchError = (operation: string, id: string, error: any): void => {
  logWarn(`Batch operation failed for ${operation}`, { id, error: error.message });
};
